import { StyleSheet } from "react-native";

import type { UiTheme } from "../theme";

/** Room under the last field of a scroller, before the keyboard's height. */
export const SCROLL_END = 64;

// The phone editor's own boxes (#336). Tokens only; rows that are taps hold
// the 44pt floor; numbers in the schema lines are mono.
export function makeV3Styles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    gutter: { paddingHorizontal: s.s5 },
    section: { paddingHorizontal: s.s5, gap: s.s4, paddingTop: s.s4 },
    headRow: { flexDirection: "row", alignItems: "center", gap: s.s3, minHeight: 44 },
    headTitle: { flex: 1, color: p.ink, fontSize: t.sizes.md, fontWeight: "700" },
    rows: { gap: s.s2 },
    row: {
      minHeight: 44,
      paddingVertical: s.s3,
      paddingHorizontal: s.s4,
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      gap: s.s1,
    },
    rowGrouped: { borderLeftWidth: 4, borderLeftColor: p.brand500 },
    // The selection (2B): the box at the left, the text beside it. Checked is
    // the ✓ in the box first; the outline only repeats it.
    rowSelectable: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    rowBody: { flex: 1, gap: s.s1 },
    rowChecked: { borderColor: p.brand500 },
    rowTop: { flexDirection: "row", alignItems: "center", gap: s.s3 },
    name: { flex: 1, color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    tag: { color: p.ink2, fontSize: t.sizes.sm, fontWeight: "600" },
    line: { color: p.ink2, fontSize: t.sizes.sm, fontFamily: t.fonts.mono },
    muted: { color: p.ink3, fontSize: t.sizes.sm },
    empty: { color: p.ink3, fontSize: t.sizes.base, paddingVertical: s.s5 },
    ladderRow: {
      flexDirection: "row",
      alignItems: "center",
      gap: s.s4,
      minHeight: 44,
      paddingHorizontal: s.s4,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: p.border,
    },
    ladderRowOpen: { backgroundColor: p.surface2 },
    ladderPress: { flex: 1, flexDirection: "row", alignItems: "center", gap: s.s4, minHeight: 44 },
    ladder: {
      borderWidth: 1,
      borderColor: p.border,
      borderRadius: r.r3,
      backgroundColor: p.surface,
      overflow: "hidden",
    },
    round: { minWidth: 36, flexShrink: 0, color: p.ink, fontSize: t.sizes.sm, fontFamily: t.fonts.mono, fontWeight: "700" },
    // #336 S2: the line text with the round type's badge after it, wrapping
    // below only when it does not fit.
    roundBody: { flex: 1, flexDirection: "row", flexWrap: "wrap", alignItems: "center", columnGap: s.s2, rowGap: s.s1, paddingVertical: s.s2 },
    roundLineInline: { flexShrink: 1, color: p.ink, fontSize: t.sizes.sm, fontFamily: t.fonts.mono },
    roundOffInline: { flexShrink: 1, color: p.ink3, fontSize: t.sizes.sm, fontStyle: "italic" },
    roundLine: { flex: 1, color: p.ink, fontSize: t.sizes.sm, fontFamily: t.fonts.mono },
    roundOff: { flex: 1, color: p.ink3, fontSize: t.sizes.sm, fontStyle: "italic" },
    // The «Per round» lens: the exercise's name (and superset) where the
    // ladder has the round.
    lensName: { flex: 1, gap: s.s1, paddingVertical: s.s2 },
    lensNameText: { color: p.ink, fontSize: t.sizes.sm, fontWeight: "600" },
    expanded: { padding: s.s3, gap: s.s3, backgroundColor: p.bg },
    dialogActions: { flexDirection: "row", justifyContent: "flex-end", gap: s.s3 },
  });
}

export type V3Styles = ReturnType<typeof makeV3Styles>;
